import { useState, useLayoutEffect, useRef, RefObject } from "react";

const hasMultipleRows = (container: HTMLElement) => {
  const children = Array.from(container.children);
  if (children.length <= 1) return false;
  const firstTop = children[0].getBoundingClientRect().top;
  return children.some(
    (child) => child.getBoundingClientRect().top !== firstTop,
  );
};

type UseChipLayoutOptions = {
  selectedOptionsLength: number;
  singleRow?: boolean;
  maxChips?: number;
};

/**
 * Manages chip layout calculations shared between MultiSelectFieldInput and
 * MultiSelectFieldTrigger. Handles single-row overflow detection, visible chip
 * count, and the "+N" hidden chip indicator.
 * @param options - Configuration for chip layout behaviour
 * @returns rowsRef to attach to the chip container, plus computed display/hidden counts
 */
export function useChipLayout({
  selectedOptionsLength,
  singleRow,
  maxChips,
}: UseChipLayoutOptions): {
  rowsRef: RefObject<HTMLDivElement>;
  displayCount: number;
  hiddenCount: number;
} {
  const rowsRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  const prevSelectedLengthRef = useRef(selectedOptionsLength);
  useLayoutEffect(() => {
    if (prevSelectedLengthRef.current !== selectedOptionsLength) {
      prevSelectedLengthRef.current = selectedOptionsLength;
      setVisibleCount(null);
    }
  }, [selectedOptionsLength]);

  useLayoutEffect(() => {
    if (!singleRow || !rowsRef.current) {
      setVisibleCount(null);
      return;
    }

    if (visibleCount === null) {
      if (hasMultipleRows(rowsRef.current)) {
        const firstRowTop =
          rowsRef.current.children[0]?.getBoundingClientRect().top;
        const firstRowCount = Array.from(rowsRef.current.children).filter(
          (c) => c.getBoundingClientRect().top === firstRowTop,
        ).length;
        setVisibleCount(Math.max(firstRowCount - 1, 0));
      }
      return;
    }

    if (hasMultipleRows(rowsRef.current) && visibleCount > 0) {
      setVisibleCount(visibleCount - 1);
    }
  }, [singleRow, selectedOptionsLength, visibleCount]);

  const maxAllowed = maxChips ?? 10;
  const displayCount =
    singleRow && visibleCount !== null
      ? Math.min(visibleCount, maxAllowed)
      : Math.min(selectedOptionsLength, maxAllowed);
  const hiddenCount = selectedOptionsLength - displayCount;

  return { rowsRef, displayCount, hiddenCount };
}
